import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useLoading } from "../../contexts/LoadingContext";
import { useTimesheetApi } from "../../hooks/useTimesheetApi";
import { useProjectApi } from "../../hooks/useProjectApi";
import { useRbac } from "../../hooks/RbacContext";
import { useResourceApi } from "../../hooks/useResourceApi";

export const useTimesheetManagerReviewViewModel = () => {
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFriday, setSelectedFriday] = useState(null);
  const [fridays, setFridays] = useState([]);
  const [successModal, setSuccessModal] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);

  const { user } = useAuth();
  const { permissions } = useRbac();
  const { showLoading, hideLoading } = useLoading();
  const { getResources } = useResourceApi();
  const {
    getFridays,
    updateTimesheetRejection,
    updateResourceTimesheetRejection,
    getResourceTimesheetsByWeekAndProject,
    getResourceTimesheetsByWeekAndResource,
    updateTimesheetApprove,
    updateResourceTimesheetApprove,
  } = useTimesheetApi();
  const { getProjectManagerByResourceId } = useProjectApi();

  const resourceRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
    ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
        parseInt(id.trim())
      )
    : [];

  const isResourceRole = resourceRoleIds.includes(
    parseInt(permissions?.globalRole?.role_id)
  );
  const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
  ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
      parseInt(id.trim())
    )
  : [];

const isSuperAdmin = superAdminRoleIds.includes(
  parseInt(permissions?.globalRole?.role_id)
);
  const fetchTimesheetData = useCallback(
    async (selectedWeek, id) => {
      if (!selectedWeek?.weekNumber || !id) return;
      showLoading();
      try {
        let response;
        if (isResourceRole) {
          response = await getResourceTimesheetsByWeekAndResource(
            selectedWeek.weekNumber,
            id.toString()
          );
        } else {
          response = await getResourceTimesheetsByWeekAndProject(
            selectedWeek.weekNumber,
            id.toString()
          );
        }

        if (!response.success) {
          throw new Error(response.error || "Failed to fetch timesheet data");
        }

        const transformedData = response.data.map((entry) => ({
          ...entry,
          resource_weekly_timesheets: entry.resource_weekly_timesheets.map(
            (timesheet) => ({
              ...timesheet,
              project_manager_comments: (
                timesheet.project_manager_comments || []
              ).map((comment) => ({
                ...comment,
                created_on: comment.created_on,
                type: "manager",
                name: comment.manager_name,
              })),
              resource_comments: (timesheet.resource_comments || []).map(
                (comment) => ({
                  ...comment,
                  created_on: comment.created_on,
                  type: "resource",
                  name: comment.resource_name,
                })
              ),
            })
          ),
        }));

        setTimesheetEntries(transformedData);
      } catch (err) {
        console.error("Error fetching timesheet data:", err);
        setTimesheetEntries([]);
        setSuccessModal({
          type: "error",
          title: "Action Failed",
          message: err.message || "Failed to fetch timesheet data",
        });
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading, isResourceRole]
  );

  const handleProjectChange = useCallback(
    (project) => {
      setSelectedProject(project);
      if (selectedFriday?.weekNumber && project?.value) {
        fetchTimesheetData(selectedFriday, project.value);
      }
    },
    [selectedFriday, fetchTimesheetData]
  );

  const handleResourceChange = useCallback(
    (resource) => {
      setSelectedResource(resource);
      if (selectedFriday?.weekNumber && resource?.value) {
        fetchTimesheetData(selectedFriday, resource.value);
      }
    },
    [selectedFriday, fetchTimesheetData]
  );

  const handleFridayChange = useCallback(
    (friday) => {
      if (!friday?.weekNumber) return;
      setSelectedFriday(friday);
      if (isResourceRole && selectedResource?.value) {
        fetchTimesheetData(friday, selectedResource.value);
      } else if (!isResourceRole && selectedProject?.value) {
        fetchTimesheetData(friday, selectedProject.value);
      }
    },
    [selectedProject, selectedResource, fetchTimesheetData, isResourceRole]
  );

  const initialize = useCallback(async () => {
    showLoading();
    try {
      const [fridaysResponse, dataResponse] = await Promise.all([
        getFridays(isSuperAdmin ? 12 : 3),
        isResourceRole
          ? getResources()
          : getProjectManagerByResourceId(
              parseInt(user.userDetails.resource_id),
              process.env.REACT_APP_PM_ROLE_ID
            ),
      ]);

      if (!fridaysResponse.success || !dataResponse.success) {
        throw new Error(fridaysResponse.error || dataResponse.error);
      }

      // const filteredFridays = fridaysResponse.data.slice(1);
      const filteredFridays = fridaysResponse.data;
      setFridays(filteredFridays);

      let formattedResources = [];
      let formattedProjects = [];

      if (isResourceRole) {
        formattedResources = dataResponse.data.map((resource) => ({
          value: resource.resource_id,
          label: `${resource.first_name} ${resource.last_name}`,
        }));
        setResources(formattedResources);
        if (formattedResources.length > 0) {
          setSelectedResource(formattedResources[0]);
        }
      } else {
        formattedProjects = dataResponse.data.map((project) => ({
          value: project.project_id,
          label: project.project_name,
        }));
        setProjects(formattedProjects);
        if (formattedProjects.length > 0) {
          setSelectedProject(formattedProjects[0]);
        }
      }

      if (filteredFridays.length > 0) {
        const initialFriday = filteredFridays[0];
        setSelectedFriday(initialFriday);

        if (isResourceRole && formattedResources.length > 0) {
          await fetchTimesheetData(initialFriday, formattedResources[0].value);
        } else if (!isResourceRole && formattedProjects.length > 0) {
          await fetchTimesheetData(initialFriday, formattedProjects[0].value);
        }
      }
    } catch (err) {
      console.error("Error initializing:", err);
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: err.message || "Failed to initialize",
      });
    } finally {
      hideLoading();
    }
  }, [
    user?.userDetails?.resource_id,
    fetchTimesheetData,
    showLoading,
    hideLoading,
    isResourceRole,
    getFridays,
    getResources,
    getProjectManagerByResourceId,
  ]);

  useEffect(() => {
    initialize();
  }, []);

  const handleReject = useCallback(
    async (resourceId) => {
      showLoading();
      try {
        const timesheetEntry = timesheetEntries.find(
          (entry) => entry.resource_id === resourceId
        );
        console.log("Timesheet entry found:", timesheetEntry);
        if (!timesheetEntry) throw new Error("Timesheet entry not found");

        let result;
        if (isResourceRole) {
          const rejectionData = {
            timesheet_week: timesheetEntry.timesheet_week,
            resource_id: parseInt(resourceId),
            status: "REJECTED",
            rejected_by: parseInt(user.userDetails.resource_id),
            rejectedComments: timesheetEntry.resource_weekly_timesheets
              .filter((timesheet) => {
                const newComments = (
                  timesheet.project_manager_comments || []
                ).filter(
                  (comment) => !comment.__typename && comment.type === "manager"
                );
                return newComments.length > 0;
              })
              .flatMap((timesheet) => {
                const newComments = (timesheet.project_manager_comments || [])
                  .filter(
                    (comment) =>
                      !comment.__typename && comment.type === "manager"
                  )
                  .sort(
                    (a, b) => parseInt(a.created_on) - parseInt(b.created_on)
                  );
                return newComments.map((comment) => ({
                  weekly_timesheet_id: timesheet.weekly_timesheet_id,
                  project_manager_comment: comment.comment,
                }));
              }),
          };
          result = await updateResourceTimesheetRejection(rejectionData);
        } else {
          const rejectionData = {
            timesheet_week: timesheetEntry.timesheet_week,
            logged_by: parseInt(resourceId),
            status: "REJECTED",
            rejected_by: parseInt(user.userDetails.resource_id),
            project_id: parseInt(selectedProject.value),
            rejectedComments: timesheetEntry.resource_weekly_timesheets
              .filter((timesheet) => {
                const newComments = (
                  timesheet.project_manager_comments || []
                ).filter(
                  (comment) => !comment.__typename && comment.type === "manager"
                );
                return newComments.length > 0;
              })
              .flatMap((timesheet) => {
                const newComments = (timesheet.project_manager_comments || [])
                  .filter(
                    (comment) =>
                      !comment.__typename && comment.type === "manager"
                  )
                  .sort(
                    (a, b) => parseInt(a.created_on) - parseInt(b.created_on)
                  );
                return newComments.map((comment) => ({
                  weekly_timesheet_id: timesheet.weekly_timesheet_id,
                  project_manager_comment: comment.comment,
                }));
              }),
          };
          result = await updateTimesheetRejection(rejectionData);
        }

        if (!result.success) throw new Error(result.error);

        setTimesheetEntries((prevEntries) =>
          prevEntries.map((entry) =>
            entry.resource_id === resourceId
              ? { ...entry, is_approved: false, is_rejected: true }
              : entry
          )
        );

        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Timesheet rejected successfully",
        });
      } catch (err) {
        setSuccessModal({
          type: "error",
          title: "Action Failed",
          message: err.message || "Failed to reject timesheet",
        });
      } finally {
        hideLoading();
      }
    },
    [
      user.userDetails.resource_id,
      timesheetEntries,
      updateTimesheetRejection,
      updateResourceTimesheetRejection,
      showLoading,
      hideLoading,
      isResourceRole,
      selectedProject,
    ]
  );

  const handleApprove = useCallback(
    async (resourceId) => {
      showLoading();
      try {
        const timesheetEntry = timesheetEntries.find(
          (entry) => entry.resource_id === resourceId
        );
        if (!timesheetEntry) throw new Error("Timesheet entry not found");

        let result;
        if (isResourceRole) {
          const approvalData = {
            timesheet_week: timesheetEntry.timesheet_week,
            resource_id: parseInt(resourceId),
            status: "APPROVED",
            approved_by: parseInt(user.userDetails.resource_id),
          };
          result = await updateResourceTimesheetApprove(approvalData);
        } else {
          const weeklyTimesheetIds =
            timesheetEntry.resource_weekly_timesheets.map(
              (timesheet) => timesheet.weekly_timesheet_id
            );
          const approvalData = {
            timesheet_week: timesheetEntry.timesheet_week,
            logged_by: parseInt(resourceId),
            status: "APPROVED",
            approved_by: parseInt(user.userDetails.resource_id),
            project_id: parseInt(selectedProject.value),
            weekly_timesheet_id: weeklyTimesheetIds,
          };
          result = await updateTimesheetApprove(approvalData);
        }

        if (!result.success) throw new Error(result.error);

        setTimesheetEntries((prevEntries) =>
          prevEntries.map((entry) =>
            entry.resource_id === resourceId
              ? { ...entry, is_approved: true, is_rejected: false }
              : entry
          )
        );

        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Timesheet approved successfully",
        });
      } catch (err) {
        setSuccessModal({
          type: "error",
          title: "Action Failed",
          message: err.message || "Failed to approve timesheet",
        });
      } finally {
        hideLoading();
      }
    },
    [
      user.userDetails.resource_id,
      timesheetEntries,
      updateTimesheetApprove,
      updateResourceTimesheetApprove,
      showLoading,
      hideLoading,
      isResourceRole,
      selectedProject,
    ]
  );

  const handleCommentSave = useCallback(
    async (resourceId, weeklyTimesheetId, comment) => {
      try {
        setTimesheetEntries((prevEntries) =>
          prevEntries.map((entry) => {
            if (entry.resource_id === resourceId) {
              return {
                ...entry,
                resource_weekly_timesheets:
                  entry.resource_weekly_timesheets.map((timesheet) => {
                    if (timesheet.weekly_timesheet_id === weeklyTimesheetId) {
                      return {
                        ...timesheet,
                        project_manager_comment: comment,
                        project_manager_comments: [
                          ...(timesheet.project_manager_comments || []),
                          {
                            weekly_timesheet_id: weeklyTimesheetId,
                            comment: comment,
                            created_on: new Date().getTime().toString(),
                            type: "manager",
                            name: "You",
                          },
                        ],
                      };
                    }
                    return timesheet;
                  }),
              };
            }
            return entry;
          })
        );
        return true;
      } catch (err) {
        console.error("Error saving comment:", err);
        return false;
      }
    },
    []
  );

  const handleError = useCallback((error) => {
    setSuccessModal({
      type: "error",
      title: "Error",
      message: error.message || "An error occurred",
    });
  }, []);

  return {
    timesheetEntries,
    projects,
    resources,
    loading,
    fridays,
    selectedFriday,
    handleFridayChange,
    handleReject,
    handleApprove,
    successModal,
    setSuccessModal,
    selectedProject,
    selectedResource,
    handleProjectChange,
    handleResourceChange,
    handleCommentSave,
    onError: handleError,
    isResourceRole,
  };
};
